/**
 * Credit-pool economics: the dials behind the Team plan's pooled,
 * prepaid AI credits. These are deliberately NOT hardcoded constants
 * scattered through the API — they live here as editable config so the
 * founder can retune the economics (grant size, markup, rollover) when
 * the utilization data comes in, without a code change to the enforcement
 * or metering layers.
 *
 * Why a version map: Stripe Prices are immutable and we never migrate an
 * existing subscription's Price (grandfathering). When the economics
 * change, add a NEW entry here keyed to a new `plan_version`, mint a new
 * Stripe Price, and route new signups to it. Existing teams keep their
 * stamped `plan_version` and therefore their original grant/markup. The
 * current launch replaced the $19 flat Team plan with the $20 credit
 * model outright (an explicit pre-launch call, no live teams to
 * grandfather), but the machinery is here so the next change doesn't have
 * to be.
 *
 * Units: the credit ledger is denominated in **millicents** to keep
 * per-call AI costs (fractions of a cent) representable as integers with
 * no floating-point drift. 1 US cent = 1_000 millicents, so $1 =
 * 100_000 millicents and a $20 grant = 2_000_000 millicents. All values
 * stay well within JS's safe-integer range even for a 50-seat pool.
 */

/** 1 US dollar expressed in millicents (1 cent = 1_000 millicents). */
export const MILLICENTS_PER_DOLLAR = 100_000;

/**
 * Minimum top-up amount ($20). Matches the $20 list price so the floor is
 * conceptually simple ("one more seat's worth"). Applies to both the
 * per-team auto-recharge `topup_amount` and manual one-off top-ups.
 * Comfortably above Stripe's $0.50 card-charge minimum, and keeps top-ups
 * meaningful relative to the card-processing fee per charge.
 */
export const MIN_TOPUP_MILLICENTS = 20 * MILLICENTS_PER_DOLLAR;

/**
 * What Lore does when a team's pool hits zero mid-cycle. Only `hard_stop`
 * (block the call) is implemented; auto-recharge covers the "keep going"
 * path. A soft/overage mode can be added here when we actually build it.
 */
export type OnEmptyPool = 'hard_stop';

/**
 * The current plan version. Stamped on the Team Price metadata and on
 * each team's billing record so we can resolve the right economics for a
 * grandfathered team. Bump this (and add a new ECONOMICS_BY_VERSION
 * entry) whenever the credit economics change.
 */
export const CURRENT_PLAN_VERSION = 'team_credit_2026_06' as const;

export type PlanVersion = string;

export type CreditEconomics = {
  /** Human pointer back to the Stripe Price lookup_key for this version. */
  planVersion: PlanVersion;
  /** Credits granted per seat each billing cycle, in millicents ($20). */
  grantPerSeatMillicents: number;
  /** Per-seat monthly subscription price, in millicents ($20). */
  seatPriceMillicents: number;
  /**
   * Markup applied to raw provider token cost to get the billable amount.
   * 10 means the metered charge (and pool draw-down) is raw_cost * 1.10.
   * This is the dial; the Stripe metered Price bills the post-markup
   * amount 1:1, so changing the markup never requires touching Stripe.
   */
  markupPct: number;
  /**
   * When false (default), the cycle's credit grant expires at period end
   * — breakage is the margin engine, so credits do not roll over.
   * Enabling rollover erodes margin and should be a deliberate choice.
   */
  rollover: boolean;
  /** Behavior when the pool empties and auto-recharge is off/unavailable. */
  onEmptyPool: OnEmptyPool;
};

/**
 * Default per-team auto-recharge settings. These are PER-TEAM and
 * admin-editable at runtime (stored on the team's billing settings row);
 * the values here are only the defaults applied when a team first gets a
 * settings row. The reload logic reads the team's stored settings, never
 * these constants directly.
 */
export const AUTO_RECHARGE_DEFAULTS = {
  /** New teams start with auto-recharge OFF (opt-in). */
  enabled: false,
  /** Fire the low-balance alert at $10 remaining. */
  triggerThresholdMillicents: 10 * MILLICENTS_PER_DOLLAR,
  /**
   * No default top-up amount — the admin must choose one when they enable
   * auto-recharge. null means "unset"; the settings screen requires a
   * value before `enabled` can be turned on.
   */
  topupAmountMillicents: null as number | null,
} as const;

/**
 * Stripe token Billing Meter wiring. The meter aggregates the
 * post-markup billable amount in millicents (`sum` of the event payload's
 * `value`). The metered Price bills those millicents 1:1, so the markup
 * lives entirely in Lore (see `applyMarkup`) and the meter/price never
 * need re-minting when the markup changes.
 *
 * The meter id and metered price id are minted by the bootstrap script
 * and supplied via env (test-mode and live-mode values differ), resolved
 * through `resolveTokenMeterId` / `resolveTokenMeteredPriceId`.
 */
export const TOKEN_METER = {
  /** Stripe meter `event_name`; also the idempotency anchor at bootstrap. */
  eventName: 'lore_ai_token_usage',
  /** The meter payload key whose `sum` Stripe aggregates. */
  valueKey: 'value',
  /** The payload key carrying the Stripe customer id. */
  customerKey: 'stripe_customer_id',
} as const;

export const ECONOMICS_BY_VERSION: Readonly<
  Record<PlanVersion, CreditEconomics>
> = Object.freeze({
  [CURRENT_PLAN_VERSION]: {
    planVersion: CURRENT_PLAN_VERSION,
    grantPerSeatMillicents: 20 * MILLICENTS_PER_DOLLAR,
    seatPriceMillicents: 20 * MILLICENTS_PER_DOLLAR,
    markupPct: 10,
    rollover: false,
    onEmptyPool: 'hard_stop',
  },
});

/** Resolve the economics for a team's stamped plan version (defaults to current). */
export const getEconomics = (
  planVersion: PlanVersion = CURRENT_PLAN_VERSION,
): CreditEconomics =>
  ECONOMICS_BY_VERSION[planVersion] ??
  ECONOMICS_BY_VERSION[CURRENT_PLAN_VERSION];

/** Credits to grant for a given seat count, in millicents. */
export const grantAmountMillicents = (
  seats: number,
  planVersion: PlanVersion = CURRENT_PLAN_VERSION,
): number =>
  Math.max(0, Math.trunc(seats)) * getEconomics(planVersion).grantPerSeatMillicents;

/**
 * Apply the configured markup to a raw provider cost to get the billable
 * amount Lore charges and draws down from the pool. Both in millicents
 * (1 cent = 1,000 millicents). Rounds up so we never under-bill a
 * fractional millicent.
 */
export const applyMarkup = (
  rawCostMillicents: number,
  planVersion: PlanVersion = CURRENT_PLAN_VERSION,
): number => {
  const { markupPct } = getEconomics(planVersion);
  // Multiply before dividing so we stay in integer space. The float form
  // `raw * (1 + markupPct / 100)` can land a hair above an exact integer
  // (e.g. 100 * 1.1 === 110.00000000000001) and over-bill by a millicent.
  return Math.ceil((rawCostMillicents * (100 + markupPct)) / 100);
};

/** Resolve the Stripe token meter id from env (test vs live differ). */
export const resolveTokenMeterId = (
  env: Record<string, string | undefined> = typeof process !== 'undefined'
    ? (process.env ?? {})
    : {},
): string | null => {
  const value = env.STRIPE_TOKEN_METER_ID;
  return value && value.length > 0 ? value : null;
};

/** Resolve the Stripe metered token Price id from env (test vs live differ). */
export const resolveTokenMeteredPriceId = (
  env: Record<string, string | undefined> = typeof process !== 'undefined'
    ? (process.env ?? {})
    : {},
): string | null => {
  const value = env.STRIPE_PRICE_TOKEN_METERED;
  return value && value.length > 0 ? value : null;
};
